import { useCallback, useEffect, useRef, useState } from "react";
import { useScoreApi, unwrap } from "../api/scoreApi";

// Card #1992 — "No Show", presiding judge (judge position C) only. Modeled
// directly on useRideUtilityEvents (card #139): a No Show is a RIDE-level
// fact (score-api's ride.noShow, not one judge's own ScoreSheet), so every
// judge position scoring the SAME ride sees the SAME mark, delivered by
// polling GET /ride/:rideId/no-show on the identical cadence. That's how a
// mark raised at C waterfalls to every other panel judge's sheet and shows
// up there as an active alert within one poll interval (card AC8/AC9).
//
// mark()/undo() are DIRECT writes, not queued through the offline sync
// engine — same choice #916's Scratch/No Show gavel items made (see
// ScoringForm.jsx's confirmTermReason): this is a live cloud write with its
// own confirm step and loading/error state, not something the autosave
// queue should replay later out of order.
export const NO_SHOW_POLL_MS = 4000;

/**
 * @param {string} rideId
 * @returns {{
 *   noShow: object|null,
 *   loading: boolean,
 *   error: Error|null,
 *   mark: () => Promise<void>,
 *   undo: () => Promise<void>,
 *   marking: boolean,
 *   markError: Error|null,
 * }}
 */
export function useRideNoShow(rideId) {
  const scoreApi = useScoreApi();
  const [noShow, setNoShow] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [marking, setMarking] = useState(false);
  const [markError, setMarkError] = useState(null);

  const rideIdRef = useRef(rideId);
  rideIdRef.current = rideId;
  const apiRef = useRef(scoreApi);
  apiRef.current = scoreApi;

  // Same stale-response guard as useRideUtilityEvents: a poll tick that
  // started before a mark()/undo() lands must not clobber the fresher state
  // with its own late-arriving read.
  const refreshGenRef = useRef(0);

  const refresh = useCallback(async () => {
    const id = rideIdRef.current;
    if (!id) return;
    const gen = ++refreshGenRef.current;
    const isStale = () => gen !== refreshGenRef.current;
    try {
      const result = unwrap(await apiRef.current.rides.getRideNoShow(id)) ?? null;
      if (isStale()) return;
      setNoShow(result);
      setError(null);
    } catch (err) {
      if (isStale()) return;
      setError(err);
    } finally {
      if (!isStale()) setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!rideId) {
      setNoShow(null);
      setLoading(false);
      return undefined;
    }
    setLoading(true);
    if (document.visibilityState !== "hidden") refresh();

    let timer = null;
    const start = () => {
      if (document.visibilityState === "hidden") return;
      if (timer == null) timer = setInterval(refresh, NO_SHOW_POLL_MS);
    };
    const stop = () => {
      if (timer != null) {
        clearInterval(timer);
        timer = null;
      }
    };
    const onVisibility = () => {
      if (document.visibilityState === "hidden") stop();
      else {
        refresh();
        start();
      }
    };
    start();
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      stop();
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [rideId, refresh]);

  const mark = useCallback(async () => {
    const id = rideIdRef.current;
    if (!id) return;
    setMarking(true);
    setMarkError(null);
    try {
      const result = unwrap(await apiRef.current.rides.markRideNoShow(id));
      setNoShow(result);
    } catch (err) {
      setMarkError(err);
      throw err;
    } finally {
      setMarking(false);
    }
  }, []);

  const undo = useCallback(async () => {
    const id = rideIdRef.current;
    if (!id) return;
    setMarking(true);
    setMarkError(null);
    try {
      await apiRef.current.rides.undoRideNoShow(id);
      setNoShow(null);
    } catch (err) {
      setMarkError(err);
      throw err;
    } finally {
      setMarking(false);
    }
  }, []);

  return { noShow, loading, error, mark, undo, marking, markError };
}

export default useRideNoShow;
